import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import useSwipe from "../hooks/useSwipe";

// Full-screen photo viewer. Any image on the site can open here so buyers can
// see the whole photo instead of a cropped thumbnail.
//
// Usage:
//   const openPhoto = useLightbox();
//   <img ... onClick={() => openPhoto(IMAGES, index)} />
// `images` is an array of { src, alt } (extra keys are ignored) or plain strings.
const LightboxContext = createContext(() => {});

export function useLightbox() {
  return useContext(LightboxContext);
}

const normalise = (list) =>
  (Array.isArray(list) ? list : [list])
    .filter(Boolean)
    .map((img) => (typeof img === "string" ? { src: img, alt: "" } : { src: img.src, alt: img.alt || "" }));

export function LightboxProvider({ children }) {
  const [images, setImages] = useState([]);
  const [index, setIndex] = useState(0);

  const open = useCallback((list, startIndex = 0) => {
    const next = normalise(list);
    if (!next.length) return;
    setImages(next);
    setIndex(Math.min(Math.max(startIndex, 0), next.length - 1));
  }, []);

  const close = useCallback(() => setImages([]), []);
  const isOpen = images.length > 0;
  const step = useCallback(
    (dir) => setIndex((i) => (i + dir + images.length) % images.length),
    [images.length]
  );

  // Esc closes, arrows navigate. Bound only while open.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowRight" && images.length > 1) step(1);
      else if (e.key === "ArrowLeft" && images.length > 1) step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, images.length, close, step]);

  // Lock background scrolling while the viewer is up, and restore the exact
  // value that was there before (some pages set their own overflow).
  useEffect(() => {
    if (!isOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);

  // Swipe left/right through the photos on touch screens; arrows stay for mouse.
  const swipe = useSwipe({
    onLeft: () => images.length > 1 && step(1),
    onRight: () => images.length > 1 && step(-1),
  });

  const current = isOpen ? images[index] : null;

  return (
    <LightboxContext.Provider value={open}>
      {children}
      {isOpen && (
        <div
          className="st-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={current.alt || "Product photo"}
          onClick={close}
          data-testid="lightbox"
          {...swipe}
        >
          <button type="button" className="st-lightbox-close" aria-label="Close photo" onClick={close}>
            <X size={20} strokeWidth={2.4} />
          </button>

          {images.length > 1 && (
            <button
              type="button"
              className="st-lightbox-nav st-lightbox-prev"
              aria-label="Previous photo"
              onClick={(e) => {
                e.stopPropagation();
                step(-1);
              }}
            >
              <ChevronLeft size={26} strokeWidth={2.4} />
            </button>
          )}

          {/* stopPropagation so clicking the photo itself doesn't close it */}
          <figure className="st-lightbox-figure" onClick={(e) => e.stopPropagation()}>
            <img src={current.src} alt={current.alt} />
            {/* Alt text stays on the img for screen readers and SEO, but is not
                printed under the photo — it read as a caption to buyers. */}
            {images.length > 1 && (
              <figcaption>
                <span className="st-lightbox-count">
                  {index + 1} / {images.length}
                </span>
              </figcaption>
            )}
          </figure>

          {images.length > 1 && (
            <button
              type="button"
              className="st-lightbox-nav st-lightbox-next"
              aria-label="Next photo"
              onClick={(e) => {
                e.stopPropagation();
                step(1);
              }}
            >
              <ChevronRight size={26} strokeWidth={2.4} />
            </button>
          )}
        </div>
      )}
    </LightboxContext.Provider>
  );
}

export default LightboxProvider;
